import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { supabase } from "@/integrations/supabase/client";

export interface TravelDesignerFilterProps {
  selectedDesigner: string | null;
  onDesignerChange: (designer: string | null) => void;
  itineraries: any[]; // Array of itineraries to extract designers from
  onFilteredItinerariesChange: (filteredItineraries: any[]) => void;
  disabled?: boolean;
}

export const TravelDesignerFilter: React.FC<TravelDesignerFilterProps> = ({
  selectedDesigner,
  onDesignerChange,
  itineraries,
  onFilteredItinerariesChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [travelDesigners, setTravelDesigners] = useState<any[]>([]);
  const [designersLoaded, setDesignersLoaded] = useState(false);
  const [loadingDesigners, setLoadingDesigners] = useState(false);

  // Load travel designers from database when dropdown opens for the first time
  const loadTravelDesigners = async () => {
    if (designersLoaded || loadingDesigners) return;
    
    setLoadingDesigners(true);
    try {
      // Get users with their basic information
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name');
      
      if (error) {
        console.error('Error loading travel designers:', error);
        return;
      }
      
      // Transform the data to match the expected format
      // For now, use email as name - in a real app you'd have proper user profiles
        const designers = (data || []).map(user => ({
          id: user.id,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown Designer',
          email: user.email || '',
          fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
          firstName: user.first_name || '',
          lastName: user.last_name || ''
        }));
      
      setTravelDesigners(designers);
      setDesignersLoaded(true);
    } catch (error) {
      console.error('Error loading travel designers:', error);
    } finally {
      setLoadingDesigners(false);
    }
  };

  // Load designers when dropdown opens
  useEffect(() => {
    if (isOpen && !designersLoaded) {
      loadTravelDesigners();
    }
  }, [isOpen, designersLoaded]);

  // Extract unique travel designers from itineraries
  const designers = useMemo(() => {
    if (travelDesigners.length === 0) return [];
    
    const uniqueDesignerIds = new Set<string>();
    
    // First, collect all unique designer IDs from travel_requests inside itineraries
    // Based on the database schema: itineraries -> travel_request_id -> travel_requests.assigned_designer_id
    itineraries.forEach(itinerary => {
      if (itinerary.travel_request && (itinerary.travel_request as any).assigned_designer_id) {
        uniqueDesignerIds.add((itinerary.travel_request as any).assigned_designer_id);
      }
    });
    
    // Then, map IDs to designer objects
    const designerObjects: any[] = [];
    uniqueDesignerIds.forEach(designerId => {
      const designer = travelDesigners.find(td => td.id === designerId);
      if (designer) {
        designerObjects.push(designer);
      }
    });
    
    return designerObjects.sort((a, b) => a.name.localeCompare(b.name));
  }, [itineraries, travelDesigners]);

  // Filter designers based on search term (search in name, email, first name, last name)
  const filteredDesigners = designers.filter(designer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      designer.name.toLowerCase().includes(searchLower) ||
      designer.email.toLowerCase().includes(searchLower) ||
      designer.fullName.toLowerCase().includes(searchLower) ||
      designer.firstName.toLowerCase().includes(searchLower) ||
      designer.lastName.toLowerCase().includes(searchLower)
    );
  });

  // Apply filter to itineraries when selectedDesigner changes
  useEffect(() => {
    if (!onFilteredItinerariesChange) return;
    
    if (!selectedDesigner) {
      // No filter selected, show all itineraries
      onFilteredItinerariesChange(itineraries);
      return;
    }
    
    // Find the designer ID from the selected name
    const designer = travelDesigners.find(td => td.name === selectedDesigner);
    if (!designer) {
      onFilteredItinerariesChange(itineraries);
      return;
    }
    
    // Filter itineraries by designer ID
    const filtered = itineraries.filter(itinerary => {
      const designerId = (itinerary.travel_request as any)?.assigned_designer_id;
      return designerId === designer.id;
    });
    
    onFilteredItinerariesChange(filtered);
  }, [selectedDesigner, itineraries, travelDesigners, onFilteredItinerariesChange]);

  const handleDesignerSelect = (designerName: string) => {
    if (selectedDesigner === designerName) {
      onDesignerChange(null);
    } else {
      onDesignerChange(designerName);
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClearFilter = () => {
    onDesignerChange(null);
    setSearchTerm("");
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="flex items-center gap-2 min-w-[140px] justify-between"
        >
          <span className="truncate">
            {selectedDesigner ? selectedDesigner : "Travel Designer"}
          </span>
          <div className="flex items-center gap-1">
            <ChevronDown className="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-3">
          <Input
            placeholder="Search designer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
          />
          <div className="max-h-48 overflow-y-auto">
            {loadingDesigners ? (
              <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                Loading designers...
              </div>
            ) : filteredDesigners.length > 0 ? (
              filteredDesigners.map((designer) => (
                <button
                  key={designer.id}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between",
                    selectedDesigner === designer.name && "bg-blue-50"
                  )}
                  onClick={() => handleDesignerSelect(designer.name)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{designer.name}</span>
                    <span className="text-xs text-gray-500">{designer.email}</span>
                  </div>
                  {selectedDesigner === designer.name && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                No designers found
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
